import { LocalStorageService } from 'angular-2-local-storage';
import { IProcedure } from '../../redux/timeline-data.model';
import { Component, Input, ViewChild, Output, EventEmitter, SimpleChanges, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { bbGenerateShortId } from '../../../../../shared/utils/bbGenerateShortId.util';

@Component({
	selector: 'event-handler',
	templateUrl: './event-handler.component.html',
	styleUrls: ['./event-handler.component.scss']
})

export class EventHandlerComponent implements OnInit, OnChanges, OnDestroy {
	@ViewChild('panel', { static: true }) panel: MatExpansionPanel;

	@Input() propertyKey: string;
	@Input() stateTrackingUid: string;					// used to 'remember open/close state for this widget
	@Input() titleEditable = false;

	@Output() propertyChanged: EventEmitter<any>;
	@Output() propertyKeyChanged: EventEmitter<any>;
	@Output() procedureContextMenu: EventEmitter<any>;
	editTitle = false;
	numProcedures = 0;

	private _previousPropertyKey = '';
	private _procedures: IProcedure[];
	@Input() set procedures(value: IProcedure[]) {

		// Check procedures:
		// - An empty list gets an extra 'Empty' procedure
		// - A List starting with a Conditional gets an extra 'Empty' procedure at the Beginning
		// - A List ending with a Conditional gets an extra 'Empty' procedure at the End
		// These 'Empty' procedures are runtime only, this wil be removed before redux, this.dispatchChange() )
		// These 'Empty' procedures are there to be able to drop actions in.
		// For more info please ask OT

		if (!value || !value.length) {
			this._procedures = [];
			this._procedures.push(this.getEmptyProcedure()); // being able to drop actions in this 'area'
			return;
		}
		this._procedures = [...value];
		if (this._procedures.length > 0) {

			let procedure: IProcedure = this._procedures[0];
			if (procedure.type === 'conditional') {
				this._procedures.unshift(this.getEmptyProcedure()); // being able to drop actions in this 'area'
			}

			procedure = this._procedures[this._procedures.length - 1];
			if (procedure.type === 'conditional') {
				this._procedures.push(this.getEmptyProcedure()); // being able to drop actions in this 'area'
			}

		}
		this.updateNumProcedures();
	}

	get procedures(): IProcedure[] {
		return this._procedures;
	}

	constructor(private localStorageService: LocalStorageService) {
		this.propertyChanged = new EventEmitter<any>();
		this.propertyKeyChanged = new EventEmitter<any>();
		this.procedureContextMenu = new EventEmitter<any>();
	}

	procedureTrackBy(i: number, item: any) {
		return item.id || this.stateTrackingUid + '_p' + i;
	}

	ngOnInit() {
		if (!this.procedures) {
			this.procedures = [];
		}
		if (this.localStorageService.get('ovp-ie-' + this.stateTrackingUid) && this.panel) {
			this.panel.open();
		}
		this.updateNumProcedures();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.procedures && !changes.procedures.firstChange) {
			this.updateNumProcedures();
		}
	}

	ngOnDestroy() {
	}

	procedureDropped(event: CdkDragDrop<string[]>): void {
		if (event.currentIndex === 0 && this.procedures[0].actions.length === 0 && this.procedures[0].condition.variables.length === 0) {
			// exeption, do not dispatch change
			return;
		}
		if (event.previousContainer === event.container) {
			if (event.previousIndex !== event.currentIndex) {
				moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
				this.splitMultipleActionsIntoSimpleProcedures();
				this.dispatchChange('Changed procedures order');
			}
		}
		this.updateNumProcedures();
	}

	// boilerplate
	// procedureDropEntered( event: CdkDragEnter<string[]> ): void {
	// 	// console.log('procedureDropEntered');
	// }

	// procedureDropExited( event: CdkDragExit<string[]> ): void {
	// 	// console.log('procedureDropExited');
	// }

	// procedureDragEnterPredicate(item: CdkDrag<IProcedure>) {
	// 	// console.log('***predicate: ', item);
	// 	return true;
	// }

	onAddProcedureItemSelected(item: any) {
		this.addProcedure(item);
	}

	onTitleClick(ev: MouseEvent, editTitleInput: HTMLInputElement) {
		if (!this.titleEditable) {
			return;
		}
		this._previousPropertyKey = this.propertyKey;
		this.editTitle = true;
		setTimeout(() => {
			editTitleInput.focus();
			editTitleInput.select();
		});
	}

	onMessageTitleChange(ev: Event) {
		this.editTitle = false;
		this.propertyKeyChanged.emit({
			oldValue: this._previousPropertyKey,
			newValue: this.propertyKey
		});
	}

	onMessageTitleEnter(ev: KeyboardEvent) {
		ev.preventDefault();
		ev.stopPropagation();
		this.editTitle = false;
		this.propertyKeyChanged.emit({
			oldValue: this._previousPropertyKey,
			newValue: this.propertyKey
		});
	}

	onTitleMouseEvent(ev: MouseEvent) {
		if (!this.titleEditable) {
			return;
		}
		ev.stopPropagation();
	}

	removeProcedure(procedure: IProcedure): void {
		const index: number = this.procedures.indexOf(procedure, 0);
		if (index > -1) {
			this.procedures.splice(index, 1);
			this.dispatchChange('Changed ' + this.propertyKey + ': ' + 'removed procedure');
		}
		this.updateNumProcedures();
	}

	afterPanelExpand(): void {
		this.localStorageService.set('ovp-ie-' + this.stateTrackingUid, 'open');
	}
	afterPanelCollapsed(): void {
		this.localStorageService.remove('ovp-ie-' + this.stateTrackingUid); // keeping it clean, only remeber open state
	}

	actionAdded(msg?: string): void {
		this.dispatchChange(msg || 'Added action');
	}

	onProcedureContextMenu(ev: Event, procedure: any): void {
		if (ev) {
			ev.preventDefault();
			ev.stopPropagation();
		}

		const index: number = this.procedures.indexOf(procedure, 0);
		this.procedureContextMenu.emit({ ev, key: this.propertyKey, index, item: procedure });
	}

	updateNumProcedures() {
		this.numProcedures = this.procedures.filter((procedure) => (procedure.type === 'conditional' || procedure.type === 'simple')).length;
	}

	procedureChanged(msg: string): void {
		this.splitMultipleActionsIntoSimpleProcedures();
		this.dispatchChange('Changed ' + this.propertyKey + ': ' + 'changed procedure ' + msg);
	}

	private splitMultipleActionsIntoSimpleProcedures(): void {

		// handle drag-drop changes to reflect design specifications and keep data in working order
		if (this.procedures) {
			let i: number = this.procedures.length;

			// Looking for simple procedures that have more then 1 action. Create new simple procedures for those extra actions.
			// This is NeedeD when an Action is dropped in a action list. This action list is splitted like so: e.g. for 3 actions:
			// from:	p->a a a
			// to:		p->a p->a p->a
			while (i--) {
				const procedure: IProcedure = this.procedures[i];
				if (procedure.type === 'simple' && procedure.actions.length > 1) {
					// add more procedures for condition-less action
					let j: number = procedure.actions.length - 1;
					while (j > 0) {
						const newProcedure: IProcedure = {
							id: bbGenerateShortId(),
							condition: {
								variables: [],
								expression: ''
							},
							actions: [],
							else_actions: [],
							type: 'simple'
						};
						newProcedure.actions.push(procedure.actions[j]);
						procedure.actions.splice(j, 1);
						this.procedures.splice(i + 1, 0, newProcedure);
						j--;
					}
				}
			}
			this.updateNumProcedures();
		}
	}

	private dispatchChange(msg: string): void {
		if (this.procedures) {
			let i: number = this.procedures.length;

			while (i--) {
				const procedure: IProcedure = this.procedures[i];
				// remove empty procedures before emitting change (to redux)
				if (procedure.type === 'empty' || (procedure.type === 'simple' && procedure.actions.length === 0)) {
					this.procedures.splice(i, 1);
				}
			}
		}
		this.propertyChanged.emit({ key: this.propertyKey, value: [...this.procedures], msg });
	}

	private addProcedure(item?: any): void {
		this.procedures.push({
			id: bbGenerateShortId(),
			condition: {
				variables: [],
				expression: ''
			},
			actions: [{
				id: bbGenerateShortId(),
				entity: item.value,
				command: '',
				parameters: []
			}],
			else_actions: [],
			type: 'simple'
		});
		this.splitMultipleActionsIntoSimpleProcedures();
		this.dispatchChange('Changed ' + this.propertyKey + ': ' + 'added procedure');
	}

	private getEmptyProcedure(): IProcedure {
		return {
			id: bbGenerateShortId(),
			condition: {
				variables: [],
				expression: ''
			},
			actions: [],
			else_actions: [],
			type: 'empty'
		};
	}
}
